import { Injectable, EventEmitter, Inject, DOCUMENT } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ApiEndpoints } from '../../../shared/common/config';
import { User } from '../../../shared/interfaces/user.interface';
import { BehaviorSubject, catchError, filter, Observable, of, ReplaySubject, Subject, switchMap, take, throwError } from 'rxjs';
import { PermissionsList } from '../../../shared/common/PermissionList';
import { environment } from 'src/environments/environment';


@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  public onGetGoogleAPIKey: EventEmitter<boolean> = new EventEmitter();
  private readonly jwtHelper: JwtHelperService;
 
  public currentUser: Observable<any>;
   private currentUserSubject = new BehaviorSubject<User | null>(null);

  public currentUser$: Observable<any> = this.currentUserSubject.asObservable();

  constructor(
    private readonly _http: HttpClient,
    @Inject(DOCUMENT) private readonly document: any,
  ) {
    this.jwtHelper = new JwtHelperService();
    const storedUser = localStorage.getItem('currentUser');
    this.currentUserSubject = new BehaviorSubject<User | null>(
      storedUser ? JSON.parse(storedUser) : null,
    );
    this.currentUser = this.currentUserSubject.asObservable();
  }

  /**
   * check if user have access to a spacific page
   *
   */
  checkAccess(pagename: string): boolean {
    let userinfo = JSON.parse(localStorage.getItem('currentUser')!);
    if (
      userinfo?.user_profile.Permissions?.includes(
        PermissionsList[pagename as keyof typeof PermissionsList],
      )
    )
      return true;
    else return false;
  }

    getApiKey(): string | null {
   let apiKey = this.getParamValueQueryString('api_key');
    if(apiKey != null){
      return apiKey;
    }
    return null;
  }

  /**
   * check if user LoggedIn
   *
   */
  checkIfLoggedIn() {
    try {
      if (!localStorage.getItem('currentUser')) {
        return false;
      }

      let currentUser = JSON.parse(localStorage.getItem('currentUser')!);
 
      if (!currentUser.accessToken) {
        return false;
      }

      if (!this.jwtHelper) {
        return false;
      }

      const isExpired = this.jwtHelper.isTokenExpired(currentUser.accessToken);

      
      if (currentUser.accessToken && !isExpired) {
        // Token is present and not expired
        return true;
      }
      
      return false;
    } catch (error) {
      console.warn('Token validation error:', error);
      return false;
    }
  }

  /**
   * login
   *
   */
  login(payload: any): Observable<any> {
    return this._http.post<any>(ApiEndpoints.authentication.login, payload);
  }

  // Lock mechanism to prevent multiple parallel refresh calls
  private isRefreshing = false;
  private refreshSubject: Subject<any> = new ReplaySubject<any>(1);

  refreshToken(): Observable<any> {
    if (this.isRefreshing) {
      return this.refreshSubject.pipe(
        filter(result => result !== null),
        take(1),
      );
    }

    this.isRefreshing = true;

    const userData = this.currentUserValue;
    const refreshTokenValue = userData?.refreshToken ?? '';
    const accessToken = userData?.accessToken ?? '';

    return this._http.post<any>(ApiEndpoints.authentication.refreshToken, { accessToken, refreshToken: refreshTokenValue }).pipe(
      switchMap((response: any) => {
        this.isRefreshing = false;
        if (response?.data && response.success) {
          this.updateRefreshedUserData(response.data);
          this.refreshSubject.next(response.data);
          return of(response.data);
        }
        const err = new Error('Failed to refresh token');
        this.refreshSubject.error(err);
        this.refreshSubject = new ReplaySubject<any>(1);
        this.removeUserData();
        return throwError(() => err);
      }),
      catchError(error => {
        this.isRefreshing = false;
        this.refreshSubject.error(error);
        this.refreshSubject = new ReplaySubject<any>(1);
        this.removeUserData();
        return throwError(() => error);
      }),
    );
  }
  /**
   * save user data
   *
   */
  saveUserData(userinfo: any) {
    localStorage.setItem('currentUser', JSON.stringify(userinfo));
  }

  updateRefreshedUserData(userinfo: any) {
    let userData = this.currentUserValue;
    if(userData != null){
      userData.accessToken = userinfo.accessToken;
      userData.refreshToken = userinfo.refreshToken;
      localStorage.setItem('currentUser', JSON.stringify(userData));
    }
  }

  /**
   * save user data
   *
   */
  removeUserData() {
    localStorage.setItem('currentUser', '');
  }

  /**
   * getter: currentUserValue
   *
   */
  public get currentUserValue(): User | null {
    if (!localStorage.getItem('currentUser')) {
      return null;
    }
    this.currentUserSubject = new BehaviorSubject<User | null>(
      JSON.parse(localStorage.getItem('currentUser')!),
    );
    this.currentUser = this.currentUserSubject.asObservable();
    return this.currentUserSubject.value;
  }

  public loggedIn() {
    return !!localStorage.getItem('currentUser');
  }

  getGoogleAPIKey(): Observable<any> {
    let propertyName = 'GoogleAPIKeySafetyUI';
    return this._http.get<any>(
      `${environment.mainapiUrl}/configurations/${propertyName}?isLocal=${environment.isLocal}`,
    );
  }

  GetGeoCode(latitude: any, longitude: any): Observable<any> {
    let addressModel = {
      address: {
        latitude: latitude,
        longitude: longitude,
      },
      addressName: '',
    };
    return this._http.post<any>(
      `${environment.mainapiUrl}/google-map/geocode`,
      addressModel,
    );
  }

  getParamValueQueryString(paramName: string) {
    const _currentUser = this.currentUserValue;
    if(_currentUser?.apiKey){
      return _currentUser.apiKey;
    }
    const url: string = this.document.location.href;
    let paramValue: any; 
    if (url.includes('?')) {
      const httpParams = new HttpParams({ fromString: url.split('?')[1] });
      paramValue = httpParams.get(paramName);
      const existingUser = _currentUser ?? {} as User;
      existingUser.apiKey = paramValue;
      this.saveUserData(existingUser);
    }
    return paramValue;
  }
}
